import React, { useMemo, useState } from 'react';
import { createTicket } from '../../lib/support/ticketsService';

const ticketTypes = [
  'Technical issue',
  'Templates / Documents',
  'Branding & Assets (Drive)',
  'Exclusive Mandates',
  'Billing',
  'Feature request',
];

const priorities = ['normal', 'urgent'];

const NewTicketWizardModal = ({ firebase, authUser, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [type, setType] = useState(ticketTypes[0]);
  const [priority, setPriority] = useState('normal');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const diagnostics = useMemo(() => {
    if (!includeDiagnostics) return {};
    return {
      route: typeof window !== 'undefined' ? window.location.pathname : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      appVersion: process.env.REACT_APP_VERSION || 'dashboard',
      timestamp: new Date().toISOString(),
    };
  }, [includeDiagnostics]);

  const handleSubmit = async () => {
    if (!authUser?.uid) return;
    setSubmitting(true);
    setError(null);
    try {
      const ticket = await createTicket(
        firebase,
        authUser.uid,
        {
          name: authUser?.name || authUser?.displayName || '',
          email: authUser?.email || '',
          phone:
            authUser?.phoneNumber ||
            authUser?.phone ||
            authUser?.profile?.phone ||
            authUser?.profile?.phoneNumber ||
            '',
          subject: subject || 'Support request',
          type,
          priority,
          description,
          diagnostics,
        },
        files,
      );
      onCreated(ticket);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="support-modal-overlay" onClick={onClose}>
      <div className="support-modal" onClick={(event) => event.stopPropagation()}>
        <div className="support-modal-header">
          <div style={{ fontWeight: 600 }}>New Ticket</div>
          <button className="support-btn" onClick={onClose}>Close</button>
        </div>
        <div className="support-modal-body">
          <div className="support-wizard-steps">
            {[1, 2, 3].map((num) => (
              <div key={num} className={`support-step ${step >= num ? 'active' : ''}`} />
            ))}
          </div>
          {step === 1 && (
            <>
              <div className="support-section-title">What do you need help with?</div>
              <div className="support-chip-row">
                {ticketTypes.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`support-chip ${type === option ? 'active' : ''}`}
                    onClick={() => setType(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
              <div className="support-field" style={{ marginTop: '1rem' }}>
                <label>Priority</label>
                <div className="support-chip-row">
                  {priorities.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`support-chip ${priority === p ? 'active' : ''}`}
                      onClick={() => setPriority(p)}
                    >
                      {p === 'normal' ? 'Normal' : 'Urgent'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="support-field">
                <label>Subject</label>
                <input value={subject} onChange={(event) => setSubject(event.target.value)} placeholder="Short summary" />
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <div className="support-section-title">Details</div>
              <div className="support-field">
                <label>Description</label>
                <textarea
                  rows={6}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="What happened? What did you expect? Steps to reproduce�"
                />
              </div>
              <div className="support-field">
                <label>
                  <input
                    type="checkbox"
                    checked={includeDiagnostics}
                    onChange={(event) => setIncludeDiagnostics(event.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Include diagnostics (browser, route, timestamp)
                </label>
              </div>
              <div className="support-field">
                <label>Attachments</label>
                <input
                  type="file"
                  multiple
                  onChange={(event) => setFiles(Array.from(event.target.files || []))}
                />
                {files.length > 0 && (
                  <div className="support-attachment-list">
                    {files.map((file) => (
                      <div key={file.name}>{file.name}</div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <div className="support-section-title">Confirm & Submit</div>
              <div style={{ color: '#dcdcdc' }}>
                <p><strong>Type:</strong> {type}</p>
                <p><strong>Priority:</strong> {priority}</p>
                <p><strong>Subject:</strong> {subject || 'Support request'}</p>
                <p><strong>Description:</strong> {description || '�'}</p>
                <p><strong>Attachments:</strong> {files.length}</p>
              </div>
              {error && <div style={{ color: '#ff6b6b' }}>{error}</div>}
            </>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.2rem' }}>
            <button className="support-btn" onClick={() => setStep((prev) => Math.max(1, prev - 1))} disabled={step === 1}>
              Back
            </button>
            {step < 3 ? (
              <button className="support-btn primary" onClick={() => setStep((prev) => Math.min(3, prev + 1))}>
                Next
              </button>
            ) : (
              <button className="support-btn primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting�' : 'Submit Ticket'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewTicketWizardModal;
